/**
 * HeaderScoringBadge Component
 *
 * Renders the scoring badge in company details headers (both mobile and desktop).
 * Displays a color-coded score label with an icon and tooltip.
 *
 * The badge shows match quality indicators with:
 * - Background color (scoreSecondaryColor)
 * - Text color (scorePrimaryColor)
 * - Icon with filter styling
 * - Tooltip with detailed scoring information
 */

import { Tooltip } from 'react-tooltip';
import { ICON_BUTTON_ICONS } from '../../../buttons/IconButton';

/**
 * @param {Object} props
 * @param {string} props.companyId - Company ID for unique element IDs
 * @param {string} props.scoreSecondaryColor - Badge background color
 * @param {string} props.scorePrimaryColor - Label text color
 * @param {string} props.scoreIconFilter - CSS filter for the icon
 * @param {string} props.scoreLabel - Badge label text
 * @param {string} props.scoreTooltipText - Tooltip content
 * @param {string} props.scoreTooltipClassName - Tooltip CSS class
 * @param {number} props.windowWidth - Current window width for tooltip positioning
 * @param {'desktop'|'mobile'} [props.variant='desktop'] - Variant for ID prefix
 */
const HeaderScoringBadge = ({
  companyId,
  scoreSecondaryColor,
  scorePrimaryColor,
  scoreIconFilter,
  scoreLabel,
  scoreTooltipText,
  scoreTooltipClassName,
  windowWidth,
  variant = 'desktop'
}) => {
  // Generate unique ID based on variant
  const badgeId =
    variant === 'mobile'
      ? `result-scoring-badge-header-mobile-${companyId}`
      : `result-scoring-badge-header-${companyId}`;

  // Determine tooltip position based on window width
  const tooltipPosition = windowWidth > 1200 ? 'bottom-center' : 'bottom-end';

  return (
    <>
      <div
        className="result-scoring-badge noselect"
        id={badgeId}
        style={{ backgroundColor: scoreSecondaryColor }}
      >
        <div className="score-icon-wrap">
          <img
            src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE}
            style={{ filter: scoreIconFilter }}
          />
        </div>
        <div className="score-label" style={{ color: scorePrimaryColor }}>
          {scoreLabel}
        </div>
      </div>
      <Tooltip
        anchorSelect={`#${badgeId}`}
        multiline={true}
        variant="light"
        place={tooltipPosition}
        opacity={1}
        content={scoreTooltipText}
        className={scoreTooltipClassName}
        clickable
      />
    </>
  );
};

export default HeaderScoringBadge;
